import { FC, ReactElement, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { FeedPost } from '@queries/PostQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useViewPostMutation } from '@mutations/PostMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useIsVisible } from '@hooks/useIsVisible';

import { useNavigate } from '@router/index';

import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import { usePostReactions } from '@components/cards/PostCard/PostCard';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { VideoPlayer } from '@components/layout/VideoPlayer/VideoPlayer';

const LIKE_VALUE = '❤️';

const EntertainerReviewCardActions: FC<{
  post: FeedPost;
}> = ({ post }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const [updatedIsLiked, setUpdatedIsLiked] = useState<boolean | undefined>();
  const { postReactionsAreReady, currentUserEmojiReaction, postReactions, updateEmojiReaction } =
    usePostReactions(post._id, true);

  const onCommentClick: (() => void) | undefined = undefined;

  const isLiked =
    typeof updatedIsLiked === 'boolean' ? updatedIsLiked : currentUserEmojiReaction === LIKE_VALUE;

  const actionButtons: {
    isVisible: boolean;
    icon: ReactElement;
    onClick: any;
    count: number;
  }[] = [
    {
      isVisible: true,
      icon: (
        <FontAwesomeIcon
          name={isLiked ? 'heart' : 'heart-outline'}
          className={isLiked ? 'is-liked' : ''}
          style={{ color: isLiked ? '#e91e63' : undefined }}
        />
      ),
      onClick: async (mouseEvent: MouseEvent) => {
        mouseEvent.stopPropagation();

        const updatedValue = !isLiked;
        track('Toggle Post Like', {
          postId: post._id,
          updatedValue,
        });
        setUpdatedIsLiked(updatedValue);
        await updateEmojiReaction(updatedValue ? LIKE_VALUE : '');
      },
      count: postReactions?.emojiReactionCounts?.[LIKE_VALUE] ?? 0, // (counts?.likes ?? 0) + (updatedIsLiked && !initialIsLike ? 1 : 0),
    },
    {
      isVisible: !!onCommentClick,
      icon: <FontAwesomeIcon name="comment-outline" />,
      onClick: (mouseEvent: MouseEvent) => {
        mouseEvent.stopPropagation();
        onCommentClick?.();
      },
      count: 0, // counts?.comments ?? 0,
    },
  ];

  return (
    <div className="border-0 pt-0">
      <div className="d-flex gap-4">
        {actionButtons
          .filter(({ isVisible }) => isVisible)
          .map(({ icon, count, onClick }, i) => (
            <div key={`action-${i}`} onClick={onClick} className="d-flex align-items-center gap-2">
              <div style={{ fontSize: 14 }}>{icon}</div>
              <div>
                <span>{count}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

const EntertainerReviewCardMedia: FC<{
  isCardVisible: boolean;
  mediaItems: FeedPost['media'];
}> = ({ isCardVisible, mediaItems }) => {
  if (mediaItems.length === 1) {
    const [mediaItem] = mediaItems;

    return (
      <div className="mt-2">
        <div
          key={mediaItem._id}
          style={{
            maxHeight: 480,
            aspectRatio: mediaItem.aspectRatio ?? 4 / 5,
          }}
        >
          {mediaItem.type === 'video' && (
            <VideoPlayer
              url={mediaItem.url}
              autoplay={isCardVisible}
              aspectRatio={mediaItem.aspectRatio ?? 5 / 4}
              containerStyle={{ borderRadius: 16, overflow: 'hidden' }}
            />
          )}
          {mediaItem.type === 'image' && (
            <AsyncImage
              src={mediaItem.url}
              style={{ borderRadius: 16, overflow: 'hidden', maxWidth: '100%', maxHeight: '100%' }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <HorizontalScroll
        items={[
          <div className="d-flex gap-2">
            {mediaItems.map((mediaItem, i) => (
              <div
                key={mediaItem._id}
                style={{
                  borderRadius: 16,
                  overflow: 'hidden',
                  maxHeight: 480,
                  aspectRatio: mediaItem.aspectRatio ?? 5 / 4,
                }}
              >
                {mediaItem.type === 'video' && (
                  <VideoPlayer
                    url={mediaItem.url}
                    autoplay={isCardVisible && i === 0}
                    aspectRatio={mediaItem.aspectRatio ?? 5 / 4}
                  />
                )}
              </div>
            ))}
          </div>,
        ]}
      />
    </div>
  );
};

export const EntertainerReviewCard: FC<{
  post: FeedPost;
}> = ({ post }) => {
  const contentDivRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(contentDivRef);
  const { viewPostAsync } = useViewPostMutation();
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    if (isVisible && currentUser?._id && !post?.viewedAt) {
      viewPostAsync({
        postId: post._id,
      });
    }
  }, [isVisible, currentUser?._id, post?.viewedAt]);

  return (
    <Card>
      <div className="p-3" ref={contentDivRef}>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          <strong>{`Shared ${dayjs(post.createdAt).fromNow()}`}</strong>
        </div>
        <div className="d-grid gap-2">
          {mapStringToParagraphs(post.text, {
            collapseTopMargin: true,
            sharedClassName: 'my-0',
          })}
        </div>
        {!!post?.media?.length && (
          <EntertainerReviewCardMedia isCardVisible={isVisible} mediaItems={post.media} />
        )}
        <div className="mt-2">
          <div className="d-flex align-items-center">
            <EntertainerReviewCardActions post={post} />
          </div>
        </div>
      </div>
    </Card>
  );
};
